import React from "react";
import { ASSETS } from "@/lib/assets";
import { CheckCircle2, Layers, Zap, ArrowRight } from "lucide-react";

const FEATURES = [
  {
    title: "Real-Time Market Sync",
    desc: "Zero-latency feeds. Mock trades reflect actual market conditions.",
    chip: "Live",
    chipClass: "bg-[#D1FAE5] text-[#065F46]",
  },
  {
    title: "AI Strategy Validation",
    desc: "Test Fifi's alerts and predictive insights before going live.",
    chip: "AI",
    chipClass: "bg-[#EDE9FE] text-[#5B21B6]",
  },
  {
    title: "Options Simulation",
    desc: "Master derivatives and hedging — without risking principal.",
    chip: "Pro",
    chipClass: "bg-[#FCE7F3] text-[#9D174D]",
  },
  {
    title: "Behavioral Analytics",
    desc: "Drawdown stats and risk-adjusted returns. Fix your biases.",
    chip: "Insight",
    chipClass: "bg-[#FFEDD5] text-[#9A3412]",
  },
];

const RiskFreeSimulation = () => {
  return (
    <section
      id="simulation"
      data-testid="simulation-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        {/* Phone mockup */}
        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto w-[300px] sm:w-[340px]">
            {/* Glow */}
            <div className="absolute inset-0 brand-gradient-soft rounded-[3rem] blur-2xl opacity-80 scale-110" />
            <div className="relative rounded-[2.5rem] bg-slate-900 p-3 shadow-2xl">
              <div className="rounded-[2rem] overflow-hidden bg-white">
                <img
                  src={ASSETS.assetScreen}
                  alt="FinX asset dashboard with $44M virtual portfolio"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            {/* Floating chips */}
            <div className="absolute -top-4 -left-6 rotate-[-8deg] bg-white rounded-2xl px-4 py-2 shadow-xl border border-slate-100">
              <div className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-bold text-slate-700">Virtual $44M</span>
              </div>
            </div>
            <div className="absolute -bottom-3 -right-5 rotate-[6deg] bg-white rounded-2xl px-4 py-2 shadow-xl border border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
                Buying Power
              </div>
              <div className="font-display font-black text-slate-900">
                $44.46M
              </div>
            </div>
          </div>
        </div>

        {/* Copy + features */}
        <div className="lg:col-span-7">
          <span data-testid="simulation-status" className="inline-flex items-center gap-2 rounded-full bg-[#D1FAE5] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#065F46]">
            <Zap size={14} /> Paper Trading · Available Now
          </span>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-[1.05]">
            Practice with{" "}
            <span className="brand-gradient-text">$1M virtual cash.</span>
            <br />
            Real markets. Zero risk.
          </h2>
          <p data-testid="simulation-availability" className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
            Paper trading is available now. Practice with virtual funds, test
            strategies, and build investing confidence without risking real money.
            Live trading is planned for November 2026 and is not yet available.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                data-testid={`sim-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl bg-white border border-slate-100 p-5 hover:border-[#A855F7]/30 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <CheckCircle2 size={20} className="text-emerald-500" />
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${f.chipClass}`}
                  >
                    {f.chip}
                  </span>
                </div>
                <h4 className="font-display font-bold text-slate-900">
                  {f.title}
                </h4>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>

          <a
            href="#hero"
            data-testid="sim-cta-link"
            className="mt-8 inline-flex items-center gap-2 font-bold text-slate-900 hover:text-[#5B21B6] transition-colors"
          >
            <Layers size={18} /> Start practicing free
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default RiskFreeSimulation;
